import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { X, Crown, Percent, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../constants';

const STORAGE_KEY = 'welcome-modal-seen-v1';
// Routes where we never show the modal (already converting / authenticating)
const SUPPRESS_ON_PATHS = ['/login', '/register', '/checkout', '/cart', '/admin'];

const WelcomeModal = () => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [promo, setPromo] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    // Suppress on auth/checkout flows
    if (SUPPRESS_ON_PATHS.some((p) => location.pathname.startsWith(p))) return;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch (e) {
      return;
    }
    let cancelled = false;
    axios.get(`${API}/promo`).then((res) => {
      if (cancelled) return;
      if (res.data?.active) {
        setPromo(res.data);
        setTimeout(() => !cancelled && setOpen(true), 1200);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [authLoading, user, location.pathname]);

  const handleClose = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) { /* noop */ }
    setOpen(false);
  };

  if (!open || !promo) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
      data-testid="welcome-modal"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-br from-[#1A0A0A] via-[#121212] to-[#0F0F0F] border border-[#D62828]/40 rounded-sm shadow-2xl p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-white p-1 rounded transition-colors"
          aria-label="Close welcome modal"
          data-testid="welcome-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex justify-center mb-4">
          <div className="relative">
            <Sparkles className="w-12 h-12 text-yellow-300 animate-pulse" />
          </div>
        </div>

        <p className="uppercase tracking-[0.25em] text-xs text-[#D62828] font-bold mb-2">Grand Opening</p>
        <h2 className="font-heading text-3xl text-white mb-3 leading-tight">
          Two gifts on us<br />— claim them in 30 seconds
        </h2>

        <div className="space-y-3 mb-6 text-left max-w-xs mx-auto">
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-white">Free {promo.free_vip_months}-Month VIP</p>
              <p className="text-xs text-gray-400">Elite Crew membership — auto-applied on signup.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Percent className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-white">{promo.discount_percent}% Off Everything</p>
              <p className="text-xs text-gray-400">Site-wide. No code needed.</p>
            </div>
          </div>
        </div>

        <Link
          to="/register"
          onClick={handleClose}
          className="block w-full bg-[#D62828] hover:bg-[#B91C1C] text-white py-3 rounded-sm font-bold uppercase tracking-wider transition-all btn-glow"
          data-testid="welcome-modal-cta"
        >
          Claim My Free VIP
        </Link>
        <button
          onClick={handleClose}
          className="mt-3 text-xs text-gray-500 hover:text-gray-300 transition-colors"
          data-testid="welcome-modal-skip"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default WelcomeModal;
